import React from 'react';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { Box, Button, Flex, Heading, LoadingOrbit, Text, Warning, UnderlineTabs, UnderlineTab, Dialog, DialogTrigger, DialogFooter, useToast, SplitButton, MediaPlayer, VideoPlayerWithTimeline, TextField, TagBadge, TagPicker, type TagColor, type TagOption, badgeVariantToTagColor } from '@chatsdet/ui';
import type {
  IssueStatus,
  IssueCategorySetting,
  FailureDiagnostic,
} from '@chatsdet/types/issues';
import type { VideoTimeline } from '@chatsdet/types';
import {
  useWorkflowRunThoughts,
  calculateThoughtTimestamps,
} from '../../pages/FlowDesigner/hooks/useWorkflowRunThoughts';
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  FlagBanner,
  FlowArrow,
  PencilSimple,
  X,
} from '@phosphor-icons/react';
import { copyIssueAsMarkdown } from './copyIssueAsMarkdown';
import { toUrlId } from '../../../lib/prefixed-ids';
import { useTicketingProviders } from '../../../hooks/useTicketingProviders';
import { useDefaultTicketSource } from '../../../features/tickets/useTickets';
import {
  useIssueDetailV2,
  useAssignIssueMutation,
  useIssueCommentsV2,
  useCreateIssueCommentMutation,
  useArchiveIssueMutation,
  useUpdateIssueStatusMutation,
  useRetryDiagnosticsMutation,
} from '../../../hooks/useIssuesV2';
import { useFlagIssueMutation } from '../../../hooks/useIssueReviewQueue';
import { useQueryClient } from '@tanstack/react-query';
import {
  useTagsQuery,
  useEntityTagsQuery,
  useSetEntityTagsMutation,
  useCreateTagMutation,
} from '../../../hooks/useTags';
import {
  usePropertySummaries,
  usePropertyCredentialSummaries,
} from '../../../hooks/usePropertyMetadata';
import { useCanonicalPageSummary } from '../../../hooks/useCanonicalPages';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveScreenshotSrc } from '../../utils/resolveScreenshotSrc';
import { resolveVideoSrc } from '../../utils/resolveVideoSrc';
import { API_URL } from '../../../config';
import { issueStatusBadgeVariants, formatStatusLabel } from './issueBadges';
import { getIssuePageDisplayUrl, resolveIssuePageUrl } from './issueUrls';
import { SeverityEditableBadge } from './SeverityEditableBadge';
import { CategoryEditableBadge } from './CategoryEditableBadge';
import { CredentialSecretPopover } from './CredentialSecretPopover';
import { TeamMemberSelect } from '../TeamMemberSelect';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { NotABugModal } from './NotABugModal';
import { OrgSwitchPrompt } from '../OrgSwitchPrompt';
import {
  OverviewTab,
  DiagnosticsTab,
  CommentsTab,
  ConsoleErrorsTab,
  NetworkErrorsTab,
} from './tabs';
import { IssueTicketingProviders } from './IssueTicketingProviders';

type IssueDrawerTab =
  | 'overview'
  | 'diagnostics'
  | 'consoleErrors'
  | 'networkErrors'
  | 'comments';

/** One row in the issue Details card: muted label, right-aligned value. */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-3 py-2">
      <Text size="sm" className="text-muted-foreground shrink-0">
        {label}
      </Text>
      <div className="min-w-0 text-sm text-foreground text-right truncate">{value}</div>
    </div>
  );
}

type IssueDrawerV2Props = {
  issueId: string;
  pageIndex: number;
  totalItems: number;
  canNavigate: boolean;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onStatusUpdated?: () => void;
  onClose?: () => void;
  /** Categories for editable category badge. */
  categories?: IssueCategorySetting[];
};

const formatRelativeTime = (value: string | null) => {
  if (!value) return '—';
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return value;
  }
};

type IssueDrawerFooterProps = {
  issueId: string;
  issueStatus: IssueStatus;
  assignedToId: string | null;
  isSuperadmin: boolean;
  onAssignmentChange?: () => void;
  onStatusChange?: () => void;
  onClose?: () => void;
};

const IssueDrawerFooter = ({
  issueId,
  issueStatus,
  assignedToId,
  isSuperadmin,
  onAssignmentChange,
  onStatusChange,
  onClose,
}: IssueDrawerFooterProps) => {
  const { toast } = useToast();
  const [notABugModalOpen, setNotABugModalOpen] = React.useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [flagModalOpen, setFlagModalOpen] = React.useState(false);
  const [flagReason, setFlagReason] = React.useState('');
  const assignMutation = useAssignIssueMutation(issueId);
  const archiveMutation = useArchiveIssueMutation(issueId);
  const statusMutation = useUpdateIssueStatusMutation(issueId);
  const flagMutation = useFlagIssueMutation();

  const handleAssignmentChange = (userId: string | null) => {
    assignMutation.mutate(userId, {
      onSuccess: () => {
        toast.success(userId ? 'Issue assigned' : 'Issue unassigned');
        onAssignmentChange?.();
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to assign issue';
        toast.error(message);
      },
    });
  };

  const handleCloseIssue = () => {
    statusMutation.mutate('closed', {
      onSuccess: () => {
        toast.success('Issue closed');
        onStatusChange?.();
        onClose?.();
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to close issue';
        toast.error(message);
      },
    });
  };

  const handleOpenIssue = () => {
    statusMutation.mutate('open', {
      onSuccess: () => {
        toast.success('Issue reopened');
        onStatusChange?.();
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to reopen issue';
        toast.error(message);
      },
    });
  };

  const handleArchive = () => {
    archiveMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Issue deleted');
        onStatusChange?.();
        onClose?.();
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to delete issue';
        toast.error(message);
      },
    });
  };

  const handleFlagForReview = () => {
    const trimmed = flagReason.trim();
    if (!trimmed) return;
    flagMutation.mutate(
      { issueId, reason: trimmed },
      {
        onSuccess: () => {
          toast.success('Issue flagged for review');
          setFlagModalOpen(false);
          setFlagReason('');
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Failed to flag issue';
          toast.error(message);
        },
      }
    );
  };

  const isActionPending =
    archiveMutation.isPending || statusMutation.isPending || flagMutation.isPending;
  const isClosedState = issueStatus === 'closed' || issueStatus === 'not_a_bug';

  return (
    <>
      <footer className="px-6 py-4 border-t border-border bg-background sticky bottom-0 z-20">
        <Flex align="center" justify="between" gap="md" wrap>
          <Flex align="center" gap="sm" className="flex-1 min-w-0">
            <Text size="sm" className="text-muted-foreground shrink-0">
              Assigned to
            </Text>
            <TeamMemberSelect
              selectedUserId={assignedToId}
              onSelect={handleAssignmentChange}
              isDisabled={assignMutation.isPending}
              className="min-w-[200px] max-w-[300px]"
            />
          </Flex>
          <SplitButton
            variant="destructive"
            size="sm"
            onPress={() => setNotABugModalOpen(true)}
            isDisabled={isActionPending}
          >
            Working as intended
            {!isClosedState ? (
              <SplitButton.Item key="close" onAction={handleCloseIssue}>
                Close
              </SplitButton.Item>
            ) : (
              <SplitButton.Item key="open" onAction={handleOpenIssue}>
                Open
              </SplitButton.Item>
            )}
            {isSuperadmin && (
              <SplitButton.Item key="flag" onAction={() => setFlagModalOpen(true)}>
                <Flex align="center" gap="xs">
                  <FlagBanner size={14} />
                  Flag for Review
                </Flex>
              </SplitButton.Item>
            )}
            <SplitButton.Item key="delete" destructive onAction={() => setDeleteConfirmOpen(true)}>
              Delete
            </SplitButton.Item>
          </SplitButton>
        </Flex>
      </footer>
      <NotABugModal
        issueId={issueId}
        isOpen={notABugModalOpen}
        onOpenChange={setNotABugModalOpen}
        onComplete={onStatusChange}
      />
      <DialogTrigger isOpen={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <span style={{ display: 'none' }} />
        <Dialog title="Delete Issue">
          <Text>Are you sure you want to delete this issue? This action cannot be undone.</Text>
          <DialogFooter>
            <Flex gap="sm">
              <Button variant="outline" onPress={() => setDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={() => {
                  setDeleteConfirmOpen(false);
                  handleArchive();
                }}
              >
                Delete
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
      <DialogTrigger
        isOpen={flagModalOpen}
        onOpenChange={(open) => {
          setFlagModalOpen(open);
          if (!open) setFlagReason('');
        }}
      >
        <span style={{ display: 'none' }} />
        <Dialog title="Flag Issue for Review">
          <Flex direction="column" gap="md">
            <Text size="sm" className="text-muted-foreground">
              Flagging this issue will add it to the superadmin review queue for investigation.
            </Text>
            <TextField
              label="Reason for flagging"
              value={flagReason}
              onChange={setFlagReason}
              placeholder="Why does this issue need review?"
            />
          </Flex>
          <DialogFooter>
            <Flex gap="sm">
              <Button variant="outline" onPress={() => setFlagModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onPress={handleFlagForReview}
                isDisabled={!flagReason.trim() || flagMutation.isPending}
              >
                Flag for Review
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
};

export function IssueDrawerV2({
  issueId,
  pageIndex: _pageIndex,
  totalItems: _totalItems,
  canNavigate,
  onNavigatePrev,
  onNavigateNext,
  onStatusUpdated,
  onClose,
  categories,
}: IssueDrawerV2Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperadmin = user?.isSuperadmin === true;
  const canViewCredentialSecrets = isSuperadmin;
  const [commentPage, setCommentPage] = React.useState(1);
  const [selectedTab, setSelectedTab] = React.useState<IssueDrawerTab>('overview');
  const [screenshotDialogOpen, setScreenshotDialogOpen] = React.useState(false);
  const [showTabShadow, setShowTabShadow] = React.useState(false);
  const [commentText, setCommentText] = React.useState('');
  const [isCopying, setIsCopying] = React.useState(false);
  const [isEditingTags, setIsEditingTags] = React.useState(false);
  const orgMembersQuery = useOrgMembers();
  const orgMembers = orgMembersQuery.data?.items ?? [];

  React.useEffect(() => {
    setCommentPage(1);
    setSelectedTab('overview');
    setScreenshotDialogOpen(false);
    setShowTabShadow(false);
    setCommentText('');
  }, [issueId]);

  React.useEffect(() => {
    setShowTabShadow(false);
  }, [selectedTab]);

  const handleTabPanelScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setShowTabShadow(event.currentTarget.scrollTop > 0);
  }, []);

  const { wrongOrgInfo, clearWrongOrgInfo, ...detailQuery } = useIssueDetailV2(issueId);
  const commentsQuery = useIssueCommentsV2(issueId, { page: commentPage, pageSize: 20 });
  const createCommentMutation = useCreateIssueCommentMutation(issueId);
  const detail = detailQuery.data;
  const issue = detail?.issue;
  const comments = commentsQuery.data?.data ?? [];
  const commentsPagination = commentsQuery.data?.pagination;
  const commentCount = detail?.commentCount ?? commentsPagination?.totalItems ?? comments.length;

  // Tag management
  const queryClient = useQueryClient();
  const tagsQuery = useTagsQuery();
  const entityTagsQuery = useEntityTagsQuery('issue', issueId);
  const setEntityTagsMutation = useSetEntityTagsMutation();
  const createTagMutation = useCreateTagMutation();

  const [localTags, setLocalTags] = React.useState<TagOption[] | null>(null);

  const allTagOptions: TagOption[] = React.useMemo(() => {
    const data = tagsQuery.data;
    if (!data?.ok || !data.data) return [];
    return data.data.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color }));
  }, [tagsQuery.data]);

  const serverTagOptions: TagOption[] = React.useMemo(() => {
    const data = entityTagsQuery.data;
    if (!data?.ok || !data.data) return [];
    return data.data.map((tag) => ({ id: tag.id, name: tag.name, color: tag.color }));
  }, [entityTagsQuery.data]);

  // Use local optimistic state when available, fall back to server data
  const selectedTagOptions = localTags ?? serverTagOptions;

  // Sync server data into local state when not editing
  React.useEffect(() => {
    if (!isEditingTags && !setEntityTagsMutation.isPending) {
      setLocalTags(null);
    }
  }, [isEditingTags, setEntityTagsMutation.isPending]);

  const handleTagsChange = React.useCallback(
    (tags: TagOption[]) => {
      setLocalTags(tags);
      setEntityTagsMutation.mutate(
        {
          entityType: 'issue',
          entityId: issueId,
          tagIds: tags.map((t) => t.id),
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['issues-v2'] }).catch(() => {});
          },
          onError: (error) => {
            toast.error(error instanceof Error ? error.message : 'Failed to save tags');
            setLocalTags(null);
          },
        }
      );
    },
    [issueId, setEntityTagsMutation, queryClient, toast]
  );

  const handleCreateTag = React.useCallback(
    async (name: string, color: TagColor): Promise<TagOption> => {
      const res = await createTagMutation.mutateAsync({ name, color });
      if (res?.ok && res.tag) {
        return { id: res.tag.id, name: res.tag.name, color: res.tag.color as TagColor };
      }
      throw new Error('Failed to create tag');
    },
    [createTagMutation]
  );

  const latestOccurrence = issue?.latestOccurrence ?? null;

  const workflowSource = latestOccurrence?.workflowSourceInfo ?? null;
  const { data: rawThoughts = [] } = useWorkflowRunThoughts(
    workflowSource?.workflowId ?? null,
    workflowSource?.workflowRunId ?? null
  );

  const screenshotKey = latestOccurrence?.screenshotStorageKey ?? null;
  const screenshotSrc = screenshotKey ? resolveScreenshotSrc(screenshotKey) : null;
  const videoKey = latestOccurrence?.videoStorageKey ?? null;
  const videoSrc = videoKey ? resolveVideoSrc(videoKey) : null;
  const videoTimeline = (latestOccurrence?.videoTimelineJson as VideoTimeline | null) ?? null;
  const thoughtsWithTimestamps = React.useMemo(
    () => calculateThoughtTimestamps(rawThoughts, videoTimeline),
    [rawThoughts, videoTimeline]
  );
  const summaryMarkdown = latestOccurrence?.ticketUserFacingSummary
    ? latestOccurrence.ticketUserFacingSummary.trim()
      ? latestOccurrence.ticketUserFacingSummary
      : null
    : null;
  const consoleLogExcerpt = latestOccurrence?.consoleLogExcerpt ?? null;
  const networkFailureUrl = latestOccurrence?.networkFailureUrl ?? null;
  const networkFailureStatus = latestOccurrence?.networkFailureStatus ?? null;
  // Always show Console/Network tabs when an occurrence exists — diagnostics
  // live in S3 (not in DB JSON columns), so the tab components lazy-load them.
  const hasOccurrence = Boolean(latestOccurrence);
  const hasDiagnostics =
    latestOccurrence?.sourceKind === 'workflow_test' ||
    Boolean(latestOccurrence?.diagnosticStatus);
  const occurrenceId = latestOccurrence?.id ?? null;
  const retryDiagnosticsMutation = useRetryDiagnosticsMutation(issueId, occurrenceId);
  const canonicalPageLink =
    issue?.propertyId && latestOccurrence?.canonicalPageId
      ? `/pages/${issue.propertyId}/canonical/${latestOccurrence.canonicalPageId}`
      : null;

  const ticketingProvidersQuery = useTicketingProviders();
  // Tickets are filed in the org's default ticket source. Until an org with several providers
  // chooses one, every connected provider is offered, as before.
  const defaultSourceQuery = useDefaultTicketSource();
  const ticketingProviders = React.useMemo(() => {
    const defaultProviderId = defaultSourceQuery.data?.data.defaultProviderId ?? null;
    const all = ticketingProvidersQuery.data?.data ?? [];
    return defaultProviderId ? all.filter((provider) => provider.id === defaultProviderId) : all;
  }, [ticketingProvidersQuery.data, defaultSourceQuery.data]);

  const handleCopyMarkdown = React.useCallback(async () => {
    if (!issue || isCopying) return;
    setIsCopying(true);
    try {
      await copyIssueAsMarkdown({ issue });
      toast.success('Copied issue as markdown');
    } catch {
      toast.error('Failed to copy issue');
    } finally {
      setIsCopying(false);
    }
  }, [issue, isCopying, toast]);

  const propertyId = issue?.propertyId ?? null;
  const credentialId = issue?.credentialId ?? null;

  const propertySummariesQuery = usePropertySummaries({
    enabled: Boolean(propertyId),
  });

  const credentialSummariesQuery = usePropertyCredentialSummaries(propertyId, {
    enabled: Boolean(propertyId && credentialId),
  });

  const propertySummary = React.useMemo(() => {
    if (!propertyId) {
      return null;
    }
    const items = propertySummariesQuery.data ?? [];
    return items.find((item) => item.id === propertyId) ?? null;
  }, [propertyId, propertySummariesQuery.data]);

  const credentialSummary = React.useMemo(() => {
    if (!credentialId) {
      return null;
    }
    const result = credentialSummariesQuery.data;
    const items = result?.items ?? [];
    return items.find((item) => item.id === credentialId) ?? null;
  }, [credentialId, credentialSummariesQuery.data]);

  const propertyLabel =
    propertySummary?.name ??
    (propertySummariesQuery.isFetching ? 'Loading property…' : (propertyId ?? 'Unknown property'));

  const canonicalPageId = latestOccurrence?.canonicalPageId ?? null;
  const canonicalPageQuery = useCanonicalPageSummary(propertyId, canonicalPageId, {
    enabled: Boolean(propertyId && canonicalPageId),
  });
  const canonicalPage = canonicalPageQuery.data ?? null;
  const issuePageHref = resolveIssuePageUrl({
    primaryUrl: latestOccurrence?.primaryUrl ?? null,
    canonicalPageUrl: canonicalPage?.url ?? null,
    propertyBaseUrl: propertySummary?.baseUrl ?? null,
  });
  const issuePageDisplayUrl = getIssuePageDisplayUrl({
    primaryUrl: latestOccurrence?.primaryUrl ?? null,
    canonicalPageUrl: canonicalPage?.url ?? null,
    propertyBaseUrl: propertySummary?.baseUrl ?? null,
  });

  const credentialAccessRestricted = credentialSummariesQuery.data?.accessDenied ?? false;

  const credentialLabel = credentialId
    ? (credentialSummary?.name ??
      (credentialSummariesQuery.isFetching
        ? 'Loading credential…'
        : credentialAccessRestricted
          ? 'Restricted credential'
          : credentialId))
    : 'None';

  const canonicalPageLabel = React.useMemo(() => {
    if (canonicalPage?.title && canonicalPage.title.trim()) {
      // Some canonical page titles embed the credential label; strip it to avoid double-mention in the breadcrumb.
      if (credentialLabel && canonicalPage.title.includes(credentialLabel)) {
        const stripped = canonicalPage.title
          .replaceAll(credentialLabel, '')
          .trim()
          .replace(/^[-–—]|[-–—]$/g, '')
          .trim();
        if (stripped) {
          return stripped;
        }
      }
      return canonicalPage.title;
    }
    const url = canonicalPage?.url ?? latestOccurrence?.primaryUrl;
    if (!url) return 'Canonical page';
    try {
      const parsed = new URL(url);
      const path = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '';
      const combined = `${parsed.hostname}${path}`;
      return combined.length > 60 ? `${combined.slice(0, 57)}…` : combined || 'Canonical page';
    } catch {
      return 'Canonical page';
    }
  }, [canonicalPage?.title, canonicalPage?.url, credentialLabel, latestOccurrence?.primaryUrl]);

  const handleAddComment = React.useCallback(() => {
    const trimmed = commentText.trim();
    if (!trimmed) return;

    createCommentMutation.mutate(trimmed, {
      onSuccess: () => {
        setCommentText('');
        setCommentPage(1);
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : 'Failed to add comment';
        toast.error(message);
      },
    });
  }, [commentText, createCommentMutation, toast]);

  if (detailQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-16">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  if (wrongOrgInfo) {
    return (
      <OrgSwitchPrompt
        resourceType="issue"
        targetOrgId={wrongOrgInfo.orgId}
        targetOrgName={wrongOrgInfo.orgName}
        redirectUrl={`/issues?issueId=${issueId}`}
        onCancel={() => {
          clearWrongOrgInfo();
          onClose?.();
        }}
      />
    );
  }

  if (detailQuery.isError || !issue) {
    return (
      <Flex direction="column" align="center" justify="center" className="py-16 space-y-2">
        <Warning size={32} className="text-destructive" />
        <Text size="sm" className="text-muted-foreground">
          Failed to load issue details.
        </Text>
      </Flex>
    );
  }

  const hasIntegrationRow =
    Boolean(workflowSource) ||
    ticketingProviders.some(
      (provider) =>
        provider.capabilities.createTicket || Object.values(provider.capabilities).some(Boolean)
    );

  // The issue Details card + integration links, rendered at the top of the
  // Overview tab (kept out of the header to reduce its density).
  const detailsBlock = (
    <section className="space-y-3">
      <div className="rounded-lg border border-border bg-background shadow-sm divide-y divide-border/60">
        <DetailRow label="Application" value={propertyLabel} />
        <DetailRow
          label="Credential"
          value={
            <CredentialSecretPopover
              propertyId={propertyId}
              credentialId={credentialId}
              label={credentialLabel}
              canViewSecrets={Boolean(canViewCredentialSecrets)}
              isRestricted={credentialAccessRestricted}
            />
          }
        />
        {canonicalPageLink ? (
          <DetailRow
            label="Page"
            value={
              <a
                href={canonicalPageLink}
                className="text-primary hover:underline"
                title={canonicalPageLabel}
              >
                {canonicalPageLabel}
              </a>
            }
          />
        ) : null}
        {latestOccurrence?.primaryUrl ? (
          <DetailRow
            label="URL"
            value={
              issuePageHref ? (
                <a
                  href={issuePageHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline font-mono text-xs"
                  title={issuePageHref}
                >
                  {issuePageDisplayUrl ?? latestOccurrence.primaryUrl}
                </a>
              ) : (
                <span className="font-mono text-xs">
                  {issuePageDisplayUrl ?? latestOccurrence.primaryUrl}
                </span>
              )
            }
          />
        ) : null}
        <DetailRow
          label="First seen"
          value={`${formatDistanceToNow(new Date(issue.firstSeenAt))} ago`}
        />
        <DetailRow label="Last seen" value={formatRelativeTime(issue.lastSeenAt)} />
      </div>

      {hasIntegrationRow ? (
        <Flex align="center" gap="md" wrap>
          {workflowSource?.workflowRunId ? (
            <Flex gap="xs" align="center">
              <FlowArrow size={15} className="shrink-0 text-muted-foreground" />
              <a
                href={`/runs/${workflowSource.workflowRunId}?workflowId=${workflowSource.workflowId}`}
                className="text-sm text-primary hover:underline"
              >
                Open full run detail
              </a>
            </Flex>
          ) : null}
          <IssueTicketingProviders issueId={issueId} providers={ticketingProviders} />
        </Flex>
      ) : null}
    </section>
  );

  return (
    <>
      <Box className="h-full flex flex-col bg-background">
        <header className="px-6 py-4 border-b border-border space-y-3 sticky top-0 z-20 bg-background">
          <Flex align="start" justify="between" gap="md" wrap="wrap">
            <Flex align="center" gap="sm" wrap className="min-w-0 flex-1">
              {issue.isArchived && (
                <TagBadge name="ARCHIVED" color="gray" hideDot size="sm" className="translate-y-[1px]" />
              )}
              <TagBadge name={formatStatusLabel(issue.status)} color={badgeVariantToTagColor(issueStatusBadgeVariants[issue.status] ?? "default")} hideDot size="sm" className="translate-y-[1px]" />
              <SeverityEditableBadge
                issue={issue}
                size="sm"
                onSuccess={() => {
                  detailQuery.refetch().catch(() => {});
                  onStatusUpdated?.();
                }}
              />
              {categories ? (
                <CategoryEditableBadge
                  issue={issue}
                  categories={categories}
                  onSuccess={() => {
                    detailQuery.refetch().catch(() => {});
                    onStatusUpdated?.();
                  }}
                />
              ) : (
                <TagBadge name={issue.category.label} color="gray" hideDot />
              )}
              {issue.workflowId ? (
                <a
                  href={`/flows/${toUrlId('wf', issue.workflowId)}`}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
                >
                  <FlowArrow size={13} />
                  View flow
                </a>
              ) : null}
            </Flex>
            <Flex align="center" gap="xs" className="shrink-0">
              <Button
                variant="icon"
                onPress={handleCopyMarkdown}
                tooltip="Copy as markdown"
                iconOnly
                isDisabled={!issue || isCopying}
              >
                <Copy size={16} />
              </Button>
              {canNavigate ? (
                <>
                  <Button variant="icon" onPress={onNavigatePrev} tooltip="Previous issue" iconOnly>
                    <ArrowLeft size={16} />
                  </Button>
                  <Button variant="icon" onPress={onNavigateNext} tooltip="Next issue" iconOnly>
                    <ArrowRight size={16} />
                  </Button>
                </>
              ) : null}
              <span className="h-5 w-px bg-border mx-1" aria-hidden />
              <Button
                variant="icon"
                onPress={() => onClose?.()}
                tooltip="Close"
                iconOnly
              >
                <X size={18} />
              </Button>
            </Flex>
          </Flex>
          <Heading level={2} className="text-xl leading-tight">
            {issue.title}
          </Heading>
          {issue.duplicateOfIssueId ? (
            <Text size="sm" className="text-muted-foreground" data-testid="issue-duplicate-of">
              Duplicate of{' '}
              <Link
                to="/issues"
                search={{ issueId: issue.duplicateOfIssueId }}
                className="text-primary hover:underline"
              >
                another issue — open it
              </Link>
            </Text>
          ) : null}
          {isEditingTags ? (
            <Flex align="center" gap="xs">
              <div className="flex-1 min-w-0">
                <TagPicker
                  tags={allTagOptions}
                  selectedTags={selectedTagOptions}
                  onChange={handleTagsChange}
                  onCreateTag={handleCreateTag}
                  isLoading={tagsQuery.isLoading || entityTagsQuery.isLoading}
                  isDisabled={setEntityTagsMutation.isPending}
                />
              </div>
              <Button
                variant="icon"
                onPress={() => setIsEditingTags(false)}
                tooltip="Done editing tags"
                iconOnly
                className="shrink-0"
              >
                <PencilSimple size={14} />
              </Button>
            </Flex>
          ) : (
            <Flex align="center" gap="xs" wrap>
              {selectedTagOptions.length > 0 ? (
                selectedTagOptions.map((tag) => (
                  <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
                ))
              ) : (
                <Text size="sm" className="text-muted-foreground">
                  No tags
                </Text>
              )}
              <Button
                variant="icon"
                onPress={() => setIsEditingTags(true)}
                tooltip="Edit tags"
                iconOnly
                className="ml-1"
              >
                <PencilSimple size={14} />
              </Button>
            </Flex>
          )}
        </header>

        <div className="flex-1 min-h-0 flex flex-col">
          <div
            className={`bg-background border-b border-border transition-shadow relative z-10 ${
              showTabShadow ? 'shadow-sm' : ''
            }`}
          >
            <UnderlineTabs
              selectedId={selectedTab}
              onSelectionChange={(id) => setSelectedTab(id as IssueDrawerTab)}
              layoutId="issue-drawer-tabs"
              className="px-6 border-b-0"
            >
              <UnderlineTab id="overview">Overview</UnderlineTab>
              {hasDiagnostics && <UnderlineTab id="diagnostics">Diagnostics</UnderlineTab>}
              <UnderlineTab id="comments">Comments ({commentCount ?? 0})</UnderlineTab>
              {hasOccurrence && <UnderlineTab id="consoleErrors">Console errors</UnderlineTab>}
              {hasOccurrence && <UnderlineTab id="networkErrors">Network errors</UnderlineTab>}
            </UnderlineTabs>
          </div>

          <div className="flex-1 overflow-auto bg-muted/30" onScroll={handleTabPanelScroll}>
            {selectedTab === 'overview' && (
              <OverviewTab
                leadingContent={detailsBlock}
                screenshotSrc={screenshotSrc}
                screenshotCaption={latestOccurrence?.screenshotCaption ?? null}
                videoSrc={videoSrc}
                videoTimeline={videoTimeline}
                thoughts={thoughtsWithTimestamps.length > 0 ? thoughtsWithTimestamps : undefined}
                summaryMarkdown={summaryMarkdown}
                reproSteps={latestOccurrence?.ticketReproSteps ?? null}
                expectedBehavior={latestOccurrence?.ticketExpectedBehavior ?? null}
                issueId={issue.id}
                apiUrl={API_URL}
                onExpandScreenshot={() => setScreenshotDialogOpen(true)}
              />
            )}

            {selectedTab === 'diagnostics' && hasDiagnostics && (
              <DiagnosticsTab
                diagnosticStatus={latestOccurrence?.diagnosticStatus ?? null}
                diagnosticData={(latestOccurrence?.diagnosticJson as FailureDiagnostic | null) ?? null}
                onRetry={() => {
                  retryDiagnosticsMutation.mutate(undefined, {
                    onSuccess: () => {
                      toast.success('Diagnostics re-run started');
                    },
                    onError: (error) => {
                      const message =
                        error instanceof Error ? error.message : 'Failed to retry diagnostics';
                      toast.error(message);
                    },
                  });
                }}
                isRetrying={retryDiagnosticsMutation.isPending}
              />
            )}

            {selectedTab === 'consoleErrors' && hasOccurrence && (
              <ConsoleErrorsTab
                issueId={issue.id}
                occurrenceId={occurrenceId}
                consoleLogExcerpt={consoleLogExcerpt}
              />
            )}

            {selectedTab === 'networkErrors' && hasOccurrence && (
              <NetworkErrorsTab
                issueId={issue.id}
                occurrenceId={occurrenceId}
                networkFailureUrl={networkFailureUrl}
                networkFailureStatus={networkFailureStatus}
              />
            )}

            {selectedTab === 'comments' && (
              <CommentsTab
                comments={comments}
                pagination={commentsPagination}
                page={commentPage}
                isLoading={commentsQuery.isLoading}
                isError={commentsQuery.isError}
                isFetching={commentsQuery.isFetching}
                commentText={commentText}
                isPosting={createCommentMutation.isPending}
                onCommentTextChange={setCommentText}
                onAddComment={handleAddComment}
                onPageChange={setCommentPage}
                mentionableMembers={orgMembers}
              />
            )}
          </div>
        </div>

        <IssueDrawerFooter
          issueId={issue.id}
          issueStatus={issue.status}
          assignedToId={issue.assignedToId}
          isSuperadmin={isSuperadmin}
          onAssignmentChange={() => {
            detailQuery.refetch().catch(() => {});
            onStatusUpdated?.();
          }}
          onStatusChange={() => {
            detailQuery.refetch().catch(() => {});
            onStatusUpdated?.();
          }}
          onClose={onClose}
        />
      </Box>
      <DialogTrigger
        isOpen={screenshotDialogOpen}
        onOpenChange={setScreenshotDialogOpen}
        size="full"
      >
        <button style={{ display: 'none' }} />
        <Dialog title="Latest occurrence screenshot">
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <Heading level={3} className="text-lg">
                {issue.title}
              </Heading>
              {latestOccurrence?.primaryUrl ? (
                <Text size="sm" className="text-muted-foreground font-mono break-words">
                  {issuePageDisplayUrl ?? latestOccurrence.primaryUrl}
                </Text>
              ) : null}
            </div>
            {screenshotSrc || videoSrc ? (
              videoSrc ? (
                <VideoPlayerWithTimeline
                  posterSrc={screenshotSrc}
                  videoSrc={videoSrc}
                  timeline={videoTimeline}
                  thoughts={thoughtsWithTimestamps.length > 0 ? thoughtsWithTimestamps : undefined}
                  alt="Expanded latest occurrence video"
                  className="w-full max-h-[80vh]"
                />
              ) : (
                <MediaPlayer
                  posterSrc={screenshotSrc}
                  videoSrc={null}
                  alt="Expanded latest occurrence screenshot"
                  className="w-full max-h-[80vh]"
                  aspectRatioClass=""
                />
              )
            ) : (
              <Box className="w-full h-64 border border-dashed border-border rounded flex items-center justify-center">
                <Text size="sm" className="text-muted-foreground">
                  No screenshot available
                </Text>
              </Box>
            )}
          </div>
        </Dialog>
      </DialogTrigger>
    </>
  );
}
